import React, { useContext } from "react";
import type { Style } from "mapbox-gl";
import queryString from "query-string";
import { useLocation } from "react-router-dom";
import { getPath } from "common/routing";
import { useLoadMapIcons } from "../../hooks/useMapIcons";
import { track } from "../../utils/tracking";
import { getCurrentURL } from "../../utils/url";
import { Icon } from "../Common/Icons/LucideIcons";
import {
  ActiveFloodLayerBadge,
  Container,
  Expand,
  SquareLink,
} from "./__styles__/Preview";
import { LayeredMap } from "./LayeredMap";
import { CustomMapLayerConfig } from "./LayeredMap/customMapLayer";
import { FirmLayerConfig } from "./LayeredMap/firmLayer";
import { LayerContext } from "./layers";
import { MapViewportContextProvider } from "./utils/MapViewportContext";
import { DEFAULT_POINT_ZOOM } from "./utils/viewportHook";

interface Props {
  height: string;
  width: string;
  latitude: number;
  longitude: number;
  zoom?: number;
  account: {
    id: string;
    defaultBaseMap: {
      mapboxStyle: Style;
    };
    firms: FirmLayerConfig["firms"];
  };
  customMaps?: CustomMapLayerConfig;
}

export default ({
  height,
  width,
  latitude,
  longitude,
  zoom = DEFAULT_POINT_ZOOM,
  account,
  customMaps,
}: Props) => {
  const { visibleFIRM, visibleRaster } = useContext(LayerContext);
  const loadMapIcons = useLoadMapIcons();
  const prevLocation = getCurrentURL(useLocation());

  const layers = {
    parcels: customMaps ? {} : { value: { point: { latitude, longitude } } },
    firms: { firms: customMaps ? [] : account.firms },
    marker: { value: { latitude, longitude } },
    ...(customMaps ? { customMaps } : {}),
  };

  const trackExpandAction = () => {
    track("Map Expanded", {
      source: "propertyProfile",
    });
  };

  const activeFloodLayerName =
    visibleFIRM()?.name ?? visibleRaster()?.layerName;

  return (
    <Container>
      <MapViewportContextProvider
        initialViewport={{
          zoom,
          latitude,
          longitude,
        }}
        refsToCompose={customMaps ? [loadMapIcons] : []}
      >
        <LayeredMap
          width={width}
          height={height}
          baseMapStyle={account.defaultBaseMap.mapboxStyle}
          account={account}
          layers={layers}
        />
      </MapViewportContextProvider>

      {!customMaps && activeFloodLayerName && (
        <ActiveFloodLayerBadge style={{ marginRight: "-8px" }}>
          {activeFloodLayerName}
        </ActiveFloodLayerBadge>
      )}
      <Expand>
        <SquareLink
          onClick={trackExpandAction}
          to={{
            pathname: getPath("explore"),
            state: {
              prevLocation,
              ...(customMaps?.value?.customMapId && {
                visibleCustomMapId: customMaps.value.customMapId,
              }),
            },
            search: queryString.stringify({
              lat: latitude,
              lng: longitude,
              firmId: visibleFIRM()?.id,
              visibleCustomMapId: customMaps?.value?.customMapId,
            }),
          }}
          data-testid="expand"
        >
          <Icon iconName="expand" color="contentPrimary" size="16" />
        </SquareLink>
      </Expand>
    </Container>
  );
};
